import { Webhook as WebhookIcon } from "@mui/icons-material";
import { Alert, Box } from "@mui/material";
import { useSnackbar } from "notistack";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { PageShell } from "@/components/common";
import { LoadingState } from "@/components/common/LoadingState";
import { WebhookForm, WebhookFormErrors } from "@/pages/admin/integrations/webhooks/WebhookForm";
import { StoreService } from "@/services/features/StoreService";
import {
  CustomerWebhook,
  CustomerWebhookUpdateRequest,
  WebhookEvent,
  WebhookService,
  WebhookType,
} from "@/services/features/WebhookService";

const EditWebhook: React.FC = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { webhookId } = useParams<{ webhookId: string }>();
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [webhook, setWebhook] = useState<CustomerWebhook | null>(null);
  const [formErrors, setFormErrors] = useState<WebhookFormErrors>({});
  const [formData, setFormData] = useState<CustomerWebhookUpdateRequest>({
    id: parseInt(webhookId || "0"),
    customerId: 0,
    url: "",
    name: "",
    event: WebhookEvent.REQUEST_CREATE,
    type: WebhookType.HTTPS,
    headers: [],
    enabled: true,
    includeData: true,
    includeDemoStores: false,
  });

  const storeService = useMemo(() => StoreService.getInstance(), []);
  const webhookService = useMemo(() => WebhookService.getInstance(), []);
  const translateRef = useRef(t);

  useEffect(() => {
    translateRef.current = t;
  }, [t]);

  useEffect(() => {
    document.title = t("management.webhooks.editWebhook.documentTitle");
  }, [t]);

  useEffect(() => {
    const loadWebhook = async () => {
      if (!webhookId) {
        setError(translateRef.current("management.webhooks.editWebhook.errors.webhookIdRequired"));
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const webhookResponse = await webhookService.getWebhookById(parseInt(webhookId));
        if (!webhookResponse.success || !webhookResponse.data) {
          setError(
            webhookResponse.message ||
              translateRef.current("management.webhooks.editWebhook.errors.webhookNotFound"),
          );
          return;
        }

        const foundWebhook = webhookResponse.data;
        const foundCustomer = await storeService.getCustomerById(foundWebhook.customerId);
        if (!foundCustomer) {
          setError(
            translateRef.current("management.webhooks.editWebhook.errors.webhookCustomerNotFound"),
          );
          return;
        }

        setWebhook(foundWebhook);
        setFormData({
          id: foundWebhook.id,
          customerId: foundWebhook.customerId,
          url: foundWebhook.url,
          name: foundWebhook.name,
          event: foundWebhook.event,
          type: foundWebhook.type || WebhookType.HTTPS,
          headers: foundWebhook.headers || [],
          enabled: foundWebhook.enabled,
          includeData: foundWebhook.includeData,
          includeDemoStores: foundWebhook.includeDemoStores,
        });
      } catch {
        setError(translateRef.current("management.webhooks.editWebhook.errors.failedLoadWebhook"));
      } finally {
        setIsLoading(false);
      }
    };

    void loadWebhook();
  }, [storeService, webhookId, webhookService]);

  const validateForm = (): boolean => {
    const nextErrors: WebhookFormErrors = {};
    if (!formData.name.trim()) {
      nextErrors.name = t("management.webhooks.createWebhook.errors.nameRequired");
    }
    if (!formData.url.trim()) {
      nextErrors.url = t("management.webhooks.createWebhook.errors.urlRequired");
    }
    setFormErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async () => {
    setError(null);
    if (!validateForm()) return;

    setIsUpdating(true);
    try {
      const response = await webhookService.updateWebhook(formData);
      if (!response.success) {
        setError(
          response.message || t("management.webhooks.editWebhook.errors.failedUpdateWebhook"),
        );
        return;
      }

      enqueueSnackbar(t("management.webhooks.editWebhook.messages.webhookUpdated"), {
        variant: "success",
      });
      void navigate("/admin/integrations/webhooks");
    } catch (requestError) {
      setError(
        requestError instanceof Error
          ? requestError.message
          : t("management.webhooks.editWebhook.errors.failedUpdateWebhook"),
      );
    } finally {
      setIsUpdating(false);
    }
  };

  const pageTitle = t("management.webhooks.editWebhook.title");

  if (isLoading) {
    return (
      <PageShell breadcrumbs="admin" title={pageTitle} icon={<WebhookIcon />}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh",
          }}
        >
          <LoadingState label={t("common.loading")} />
        </Box>
      </PageShell>
    );
  }

  if (!webhook) {
    return (
      <PageShell breadcrumbs="admin" title={pageTitle} icon={<WebhookIcon />}>
        <Alert severity="error">
          {error || t("management.webhooks.editWebhook.errors.webhookNotFound")}
        </Alert>
      </PageShell>
    );
  }

  return (
    <PageShell breadcrumbs="admin" title={pageTitle} icon={<WebhookIcon />}>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      <WebhookForm
        data={formData}
        errors={formErrors}
        isEdit
        isSubmitting={isUpdating}
        submitLabel={t("management.webhooks.editWebhook.actions.updateWebhook")}
        onFieldChange={(field, value) => {
          setFormData((current) => ({ ...current, [field]: value }));
          if (field === "name" || field === "url") {
            setFormErrors((current) => ({ ...current, [field]: undefined }));
          }
        }}
        onAddHeader={() =>
          setFormData((current) => ({
            ...current,
            headers: [...current.headers, { key: "", value: "" }],
          }))
        }
        onRemoveHeader={(index) =>
          setFormData((current) => ({
            ...current,
            headers: current.headers.filter((_, headerIndex) => headerIndex !== index),
          }))
        }
        onUpdateHeader={(index, field, value) =>
          setFormData((current) => ({
            ...current,
            headers: current.headers.map((header, headerIndex) =>
              headerIndex === index ? { ...header, [field]: value } : header,
            ),
          }))
        }
        onSubmit={() => void handleSubmit()}
      />
    </PageShell>
  );
};

export default EditWebhook;
